import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  serverTimestamp
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { auth, db } from '../config/firebase';
import { 
  CompanyImage, 
  CompanyImageUploadData, 
  CompanyImageType, 
  UserProfile 
} from '../types/models';
import { FirebaseService } from './FirebaseService';

export class CompanyImageService {
  
  // Helper to ensure user is authenticated and in a company
  private static async ensureCompanyAccess(): Promise<{ userId: string; companyId: string; profile: UserProfile }> {
    if (!auth.currentUser) {
      throw new Error('You must be logged in to access company images');
    }
    
    const userId = auth.currentUser.uid;
    // Use the same fetchUserProfile method that works for shared reports
    const profile = await FirebaseService.fetchUserProfile();
    
    if (!profile?.companyId) {
      throw new Error('You must be a member of a company to access company images. Please join or create a company first.');
    }
    
    return { userId, companyId: profile.companyId, profile };
  }
  
  // Helper to handle Firebase errors with user-friendly messages
  private static handleFirebaseError(error: any, operation: string): never {
    console.error(`Error during ${operation}:`, error);
    
    // Check for permission denied errors
    if (error.code === 'permission-denied' || error.message?.includes('permission-denied')) {
      throw new Error('You do not have permission to perform this action. Please ensure you are a member of the company.');
    }
    
    // Check for authentication errors
    if (error.code === 'unauthenticated' || error.message?.includes('unauthenticated')) {
      throw new Error('You must be logged in to perform this action.');
    }
    
    // Check for not found errors
    if (error.code === 'not-found') {
      throw new Error('The requested resource was not found.');
    }
    
    // Generic error with operation context
    throw new Error(`Failed to ${operation}. ${error.message || 'Please try again.'}`);
  }
  
  // Upload a new company image
  static async uploadCompanyImage(uploadData: CompanyImageUploadData): Promise<CompanyImage> {
    const { userId, companyId, profile } = await this.ensureCompanyAccess();
    
    // Validate file
    this.validateImageFile(uploadData.file);
    
    // Create unique filename (matching Swift logic)
    const timestamp = Date.now();
    const fileExtension = uploadData.file.name.split('.').pop() || 'jpg';
    const baseName = uploadData.file.name.substring(0, uploadData.file.name.lastIndexOf('.')) || uploadData.file.name;
    const fileName = `${uploadData.imageType}_${baseName}_${timestamp}.${fileExtension}`;
    const storagePath = `companies/${companyId}/images/${uploadData.imageType}/${fileName}`;
    
    try {
      // Upload to Firebase Storage
      const storage = getStorage();
      const storageRef = ref(storage, storagePath);
      const snapshot = await uploadBytes(storageRef, uploadData.file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Get image dimensions
      const dimensions = await this.getImageDimensions(uploadData.file);
      
      // Create company image document with field names matching iOS
      const companyImageData: Omit<CompanyImage, 'id'> = {
        companyId,
        fileName: uploadData.fileName,
        originalFileName: uploadData.file.name,
        url: downloadURL,
        storagePath: storagePath,
        imageType: uploadData.imageType,
        uploadedAt: new Date(),
        updatedAt: new Date(),
        uploadedBy: userId,
        uploadedByName: profile.displayName || profile.email,
        uploadedByEmail: profile.email,
        fileSize: uploadData.file.size,
        width: dimensions?.width,
        height: dimensions?.height,
        mimeType: uploadData.file.type,
        isActive: true,
        description: uploadData.description || undefined,
        tags: uploadData.tags || [],
        order: 0
      };
      
      // Save to Firestore with field mapping for iOS compatibility
      const firestoreData: Record<string, any> = {
        company_id: companyImageData.companyId,
        file_name: companyImageData.fileName,
        original_file_name: companyImageData.originalFileName,
        url: companyImageData.url,
        storage_path: companyImageData.storagePath,
        category: companyImageData.imageType, // iOS uses 'category' instead of 'imageType'
        uploaded_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        uploaded_by: companyImageData.uploadedBy,
        uploaded_by_name: companyImageData.uploadedByName,
        uploaded_by_email: companyImageData.uploadedByEmail,
        file_size: companyImageData.fileSize,
        width: companyImageData.width,
        height: companyImageData.height,
        mime_type: companyImageData.mimeType,
        is_active: companyImageData.isActive,
        tags: companyImageData.tags,
        order: companyImageData.order
      };

      // Only add description if it's not undefined/empty
      if (companyImageData.description) {
        firestoreData.description = companyImageData.description;
      }

      const docRef = await addDoc(
        collection(db, 'companies', companyId, 'images'),
        firestoreData
      );
      
      return {
        id: docRef.id,
        ...companyImageData
      };
    } catch (error: any) {
      this.handleFirebaseError(error, 'upload image');
    }
  }
  
  // Get all company images
  static async getCompanyImages(imageType?: CompanyImageType): Promise<CompanyImage[]> {
    const { companyId } = await this.ensureCompanyAccess();
    
    try {
      let q = query(
        collection(db, 'companies', companyId, 'images'),
        where('is_active', '==', true)
        // Temporarily removed orderBy while index builds
        // orderBy('uploaded_at', 'desc')
      );
      
      // Filter by type if specified
      if (imageType) {
        q = query(
          collection(db, 'companies', companyId, 'images'),
          where('is_active', '==', true),
          where('category', '==', imageType) // iOS uses 'category' field
          // Temporarily removed orderBy while index builds
          // orderBy('uploaded_at', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      
      const images = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          companyId: data.company_id || data.companyId,
          fileName: data.file_name || data.fileName || data.imageName,
          originalFileName: data.original_file_name || data.originalFileName || data.fileName || data.imageName,
          url: data.url || data.imageUrl,
          storagePath: data.storage_path || data.storagePath || data.imagePath,
          imageType: data.category || data.imageType,
          uploadedAt: data.uploaded_at?.toDate() || data.uploadedAt?.toDate() || data.uploadDate?.toDate() || new Date(),
          updatedAt: data.updated_at?.toDate() || data.updatedAt?.toDate() || data.uploadedAt?.toDate() || data.uploadDate?.toDate() || new Date(),
          uploadedBy: data.uploaded_by || data.uploadedBy || data.createdBy,
          uploadedByName: data.uploaded_by_name || data.uploadedByName || data.createdByName,
          uploadedByEmail: data.uploaded_by_email || data.uploadedByEmail,
          fileSize: data.file_size || data.fileSize,
          width: data.width || data.dimensions?.width,
          height: data.height || data.dimensions?.height,
          mimeType: data.mime_type || data.mimeType,
          isActive: data.is_active !== undefined ? data.is_active : data.isActive !== undefined ? data.isActive : true,
          description: data.description,
          tags: data.tags || [],
          order: data.order || 0
        } as CompanyImage;
      });
      
      return images;
    } catch (error: any) {
      this.handleFirebaseError(error, 'fetch company images');
    }
  }
  
  // Update company image metadata
  static async updateCompanyImage(imageId: string, updates: Partial<Pick<CompanyImage, 'fileName' | 'imageType'>>): Promise<void> {
    const { companyId } = await this.ensureCompanyAccess();
    
    try {
      const docRef = doc(db, 'companies', companyId, 'images', imageId);
      
      // Map the updates to the correct field names for iOS compatibility
      const firestoreUpdates: Record<string, any> = {
        updated_at: serverTimestamp()
      };
      
      if (updates.fileName) {
        firestoreUpdates.file_name = updates.fileName;
      }
      
      if (updates.imageType) {
        firestoreUpdates.category = updates.imageType; // iOS uses 'category'
      }
      
      await updateDoc(docRef, firestoreUpdates);
    } catch (error: any) {
      this.handleFirebaseError(error, 'update image');
    }
  }
  
  // Helper to extract storage path from Firebase Storage URL
  private static extractStoragePathFromURL(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/o/');
      if (pathParts.length > 1) {
        const encodedPath = pathParts[1].split('?')[0];
        return decodeURIComponent(encodedPath);
      }
    } catch (error) {
      console.error('Error extracting storage path from URL:', error);
    }
    return null;
  }

  // Delete company image
  static async deleteCompanyImage(imageId: string): Promise<void> {
    const { companyId } = await this.ensureCompanyAccess();
    
    try {
      // Get image document first to get storage path
      const docRef = doc(db, 'companies', companyId, 'images', imageId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Image not found');
      }
      
      const data = docSnap.data();
      
      // Get storage path from document or extract from URL
      let storagePath = data.storage_path || data.storagePath || data.imagePath;
      
      // Validate storage path is not empty
      if (!storagePath || storagePath.trim().length === 0) {
        console.warn('Empty storage path, attempting to extract from URL');
        const url = data.url || data.imageUrl;
        if (url) {
          storagePath = this.extractStoragePathFromURL(url);
          console.log('Extracted storage path:', storagePath);
        }
      }
      
      // Delete from storage if we have a valid path
      if (storagePath && storagePath.trim().length > 0) {
        try {
          const storage = getStorage();
          const storageRef = ref(storage, storagePath);
          await deleteObject(storageRef);
          console.log('Successfully deleted from storage:', storagePath);
        } catch (storageError) {
          console.warn('Failed to delete from storage (file may not exist):', storageError);
          // Don't throw error for storage deletion failure - continue with Firestore deletion
        }
      } else {
        console.warn('No valid storage path found, skipping storage deletion');
      }
      
      // Delete document from Firestore
      await deleteDoc(docRef);
      console.log('Successfully deleted from Firestore:', imageId);
      
    } catch (error: any) {
      this.handleFirebaseError(error, 'delete image');
    }
  }
  
  // Soft delete (mark as inactive)
  static async softDeleteCompanyImage(imageId: string): Promise<void> {
    const { companyId } = await this.ensureCompanyAccess();
    
    try {
      const docRef = doc(db, 'companies', companyId, 'images', imageId);
      await updateDoc(docRef, {
        is_active: false, // Use snake_case for iOS compatibility
        deleted_at: serverTimestamp()
      });
    } catch (error: any) {
      this.handleFirebaseError(error, 'delete image');
    }
  }
  
  // Get company images by type for PDF editor
  static async getCompanyImagesByType(imageType: CompanyImageType): Promise<CompanyImage[]> {
    return this.getCompanyImages(imageType);
  }
  
  // Validate uploaded image file
  private static validateImageFile(file: File): void {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPG, PNG, SVG, or WebP images only.');
    }
    
    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload images smaller than 10MB.');
    }
  }
  
  // Get image dimensions
  private static getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(file);
    });
  }
}
